import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  Card, 
  CardMedia, 
  Box, 
  Typography, 
  CircularProgress, 
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material'
import { 
  Refresh as RefreshIcon, 
  Error as ErrorIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { useHorseImage } from '../hooks/useHorseImage'
import logoAzul from '../assets/images/logoAzulNovo.png'


interface HorseImageProps {
  tenantId: number
  horseId: number
  horseName: string
  width?: number | string
  height?: number | string
  autoLoad?: boolean
  showRetryButton?: boolean
  circular?: boolean
  showZoomButton?: boolean
  showEditButton?: boolean
  onEditImage?: () => void
}

export const HorseImage: React.FC<HorseImageProps> = ({ 
  tenantId,
  horseId,
  horseName,
  width = '100%',
  height = 200,
  autoLoad = true,
  showRetryButton = true,
  circular = true,
  showZoomButton = true,
  showEditButton = true,
  onEditImage
}) => {
  const { t } = useTranslation()
  const { imageUrl, loading, error, retry } = useHorseImage(tenantId, horseId, autoLoad)
  const [zoomModalOpen, setZoomModalOpen] = useState(false)
  const [isZoomed, setIsZoomed] = useState(false)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const imageRef = useRef<HTMLImageElement>(null)
  
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  // LOGS PARA DEBUG
  console.log('🔍 HorseImage Debug:', {
    tenantId,
    horseId,
    horseName,
    imageUrl,
    loading,
    error,
    circular,
    'imageUrl type': typeof imageUrl,
    'imageUrl value': imageUrl,
    'is null': imageUrl === null,
    'is undefined': imageUrl === undefined,
    'is empty string': imageUrl === '',
    'is falsy': !imageUrl
  })

  // Função para calcular o tamanho do círculo mantendo proporção 1:1
  const getCircularSize = () => {
    if (typeof width === 'number' && typeof height === 'number') {
      return Math.min(width, height)
    }
    if (typeof height === 'number') {
      return height
    }
    if (typeof width === 'number') {
      return width
    }
    return 200
  }

  const circularSize = getCircularSize()

  // Função para lidar com o movimento do mouse na imagem
  const handleMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!imageRef.current) return
    const rect = imageRef.current.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    setMousePosition({ x, y })
  }

  // Função para lidar com o clique de edição
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onEditImage) {
      onEditImage()
    }
  }

  // Estado de carregamento
  if (loading) {
    console.log('🟡 Renderizando estado LOADING')
    return (
      <Box sx={{ 
        width: circular ? circularSize : width,
        height: circular ? circularSize : height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card sx={{ 
          width: circular ? circularSize : '100%',
          height: circular ? circularSize : '100%',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'grey.50',
          borderRadius: circular ? '50%' : 1
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">
              {t("common.loading")}
            </Typography>
          </Box>
        </Card>
      </Box>
    )
  }

  // Estado de erro OU sem imagem
  if (error || !imageUrl) {
    // Se o erro é "Imagem não encontrada" ou não há imageUrl, mostra o logo
    if (error === "Imagem não encontrada" || !imageUrl) {
      return (
        <Box sx={{ 
          width: circular ? circularSize : width,
          height: circular ? circularSize : height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Card sx={{ 
            width: circular ? circularSize : '100%',
            height: circular ? circularSize : '100%',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: 'grey.100',
            borderRadius: circular ? '50%' : 1,
            position: 'relative',
            overflow: 'hidden',
            p: 0
          }}>
            {/* LOGO preenchendo todo o espaço */}
            <Box
              component="img"
              src={logoAzul}
              alt="Logo"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover', // Preenche todo o espaço
                opacity: 1, // Opacidade total
                filter: 'none' // Sem filtros
              }}
              onLoad={() => console.log('✅ Logo carregado com sucesso!')}
              onError={(e) => console.error('❌ Erro ao carregar logo:', e)}
            />
          </Card>
        </Box>
      )
    }
    
    // Para outros tipos de erro, mostra a tela de erro tradicional
    console.log('🔴 Renderizando estado ERROR:', error)
    return (
      <Box sx={{ 
        width: circular ? circularSize : width,
        height: circular ? circularSize : height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card sx={{ 
          width: circular ? circularSize : '100%',
          height: circular ? circularSize : '100%',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'grey.100',
          position: 'relative',
          borderRadius: circular ? '50%' : 1
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ fontSize: 48, color: 'grey.400' }} />
            {!circular && (
              <Typography variant="caption" color="text.secondary" textAlign="center">
                {error}
              </Typography>
            )}
            {showRetryButton && (
              <Tooltip title={t("stream.retry")}>
                <IconButton size="small" onClick={retry} sx={{ mt: 1 }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Card>
      </Box>
    )
  }

  // Componente da imagem circular
  const CircularImage = () => (
    <Box sx={{ 
      position: 'relative', 
      display: 'inline-flex',
      width: circularSize,
      height: circularSize,
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Avatar
        src={imageUrl}
        alt={t("horses.photoAlt", { name: horseName })}
        sx={{
          width: circularSize,
          height: circularSize,
          cursor: showZoomButton ? 'pointer' : 'default',
          border: '3px solid',
          borderColor: 'primary.main',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': showZoomButton ? {
            transform: 'scale(1.05)',
            boxShadow: theme.shadows[4]
          } : {}
        }}
        onClick={showZoomButton ? () => setZoomModalOpen(true) : undefined}
      />
      

    </Box>
  )

  // Componente da imagem retangular
  const RectangularImage = () => (
    <Card sx={{ width, height: 'auto', maxHeight: height, position: 'relative' }}>
      <CardMedia
        component="img"
        image={imageUrl}
        alt={t("horses.photoAlt", { name: horseName })}
        sx={{
          width: '100%',
          height,
          objectFit: 'cover',
          objectPosition: 'center',
          cursor: showZoomButton ? 'pointer' : 'default',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': showZoomButton ? {
            transform: 'scale(1.02)'
          } : {}
        }}
        onClick={showZoomButton ? () => setZoomModalOpen(true) : undefined}
      />
    </Card>
  )

  console.log('🎯 Renderizando componente final:', { 
    circular, 
    'vai renderizar': circular ? 'CircularImage' : 'RectangularImage' 
  })

  return (
    <>
      {circular ? <CircularImage /> : <RectangularImage />}
      
      {/* Modal de Zoom */}
      <Dialog
        open={zoomModalOpen}
        onClose={() => setZoomModalOpen(false)}
        maxWidth={false}
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            margin: 0,
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '100%',
            height: '100%'
          }
        }}
      >
        <DialogContent
          sx={{
            p: { xs: 1, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: fullScreen ? '100vh' : '85vh',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'rgba(0, 0, 0, 0.9)'
          }}
        >
          <IconButton
            onClick={() => setZoomModalOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 10,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.9)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              color: 'white',
              zIndex: 10,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              px: 2,
              py: 1,
              borderRadius: 1,
              maxWidth: '60%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {horseName}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              color: 'white',
              zIndex: 10,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              px: 2,
              py: 1,
              borderRadius: 1
            }}
          >
            {t("horses.zoomHint")}
          </Typography>

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              overflow: 'hidden'
            }}
          >
            <Box
              component="img"
              ref={imageRef}
              src={imageUrl}
              alt={t("horses.photoAlt", { name: horseName })}
              onMouseMove={handleMouseMove}
              onMouseEnter={() => setIsZoomed(true)}
              onMouseLeave={() => setIsZoomed(false)}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: 1,
                cursor: 'zoom-in',
                transition: 'transform 0.1s ease-out',
                transform: isZoomed ? 'scale(2)' : 'scale(1)',
                transformOrigin: `${mousePosition.x}% ${mousePosition.y}%`,
                imageRendering: 'high-quality',
                loading: 'eager'
              }}
            />
          </Box>
        </DialogContent>
        
        {!fullScreen && (
          <DialogActions sx={{ backgroundColor: 'rgba(0, 0, 0, 0.9)', p: 2 }}>
            <Button
              onClick={() => setZoomModalOpen(false)}
              variant="contained"
              color="primary"
            >
              {t("common.close")}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  )
}